import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import React from 'react';

import { TIPTAP_GALLERY_TYPE } from './../ricos-content';
import { Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import { GalleryOptions_Layout_Type } from './../ricos-schema/next-schema';
import type { PublicCommands, RicosServices, ToolbarButton } from './../ricos-types';
import { LINK_MODAL_ID } from './../_shared/link-modal/constants';
import { MODAL_ROOT } from './../_shared/link-modal/data-hooks/data-hooks';
import { PLUGIN_TOOLBAR_BUTTON_ID } from './../editor-common';
import {
  getVisibleOnlyOnDesktopResolver,
  selectedNodeResolver,
} from './../plugin-commons';
import { handleGalleryUpload } from './/handle-gallery-upload';
import {
  AlignmentPanel,
  NodeAlignmentButton,
  NodeSizeButton,
  SizePanel,
} from './../toolbars-ui';
import { FLOATING_PLUGIN_TOOLBAR } from './../toolbars-v3';

import type { SettingsType } from './consts';
import {
  fileInputAccept,
  GALLERY_LAYOUT_DROPDOWN,
  GALLERY_LAYOUT_SETTINGS_MODAL,
  GALLERY_MEDIA_SETTINGS_MODAL,
  GALLERY_SETTINGS_MODAL_ID,
  SETTINGS_TAB,
} from './consts';
import {
  PLUGIN_TOOLBAR_ADD_MEDIA_BUTTON,
  PLUGIN_TOOLBAR_LAYOUT_DROPDOWN_BUTTON,
  PLUGIN_TOOLBAR_MANAGE_MEDIA_BUTTON,
} from './data-hooks';
import { layoutRicosData } from './layout-data-provider';
import { GallerySettingsModal } from './modals/settings';
import GalleryAddMediaButton from './toolbar/buttons/GalleryAddMediaButton';
import { GalleryLayoutButton } from './toolbar/buttons/GalleryLayoutButton';
import GalleryManageMediaButton from './toolbar/buttons/GalleryManageMediaButton';
import { LayoutDropdown } from './toolbar/LayoutDropdown';
import type { GalleryPluginEditorConfig } from './types';

const selectedLayoutResolver = {
  id: 'selectedLayout',
  resolve: (content: $TSFixMe) => {
    const galleryNode =
      Array.isArray(content) && content.find(node => node.type.name === Node_Type.GALLERY);
    if (!galleryNode) {
      return undefined;
    }
    return galleryNode.attrs.options?.layout.type;
  },
};

const isValidIndex = (index?: number): index is number => typeof index === 'number' && index >= 0;

export const getToolbarButtons = (
  config: GalleryPluginEditorConfig,
  services: RicosServices
): ToolbarButton[] => {
  const { accept = fileInputAccept } = config;
  const { isMobile } = services?.context ?? {};

  const { modals } = services;

  const toggleSettingsModal = ({
    modalId,
    node,
    referenceElement,
    activeTab,
    clickFromKeyboard,
    publicCommands,
  }: {
    modalId: string;
    node: ProseMirrorNode;
    referenceElement: HTMLElement;
    activeTab: SettingsType;
    clickFromKeyboard: boolean;
    publicCommands: PublicCommands;
  }) => {
    if (modals?.isModalOpen(modalId)) {
      modals?.closeModal(modalId);
      return;
    }

    modals?.openModal({
      id: modalId,
      Component: GallerySettingsModal,
      withTransition: true,
      enableDrag: !isMobile,
      dragHandleSelector: '[data-drag-handler]',
      layout: isMobile ? 'fullscreen' : 'floating',
      componentProps: {
        nodeId: node.attrs.id,
        modalId,
        onFileSelect: index => {
          handleGalleryUpload({
            publicCommands,
            services,
            config,
            nodeId: node.attrs.id,
            fileState: isValidIndex(index) ? { itemIndex: index } : undefined,
          });
        },
        accept,
        activeTab,
      },
      positioning: {
        referenceElement:
          referenceElement.closest(`[data-hook~=${FLOATING_PLUGIN_TOOLBAR}]`) ?? referenceElement,
        placement: 'right-start',
      },
      focusReturnElement: referenceElement,
      returnFocusOnDeactivate: clickFromKeyboard,
      disableArrowNavigation: true,
      onClickOutside: {
        on: 'pointerup',
        handler: e => {
          if (
            e.target instanceof Element &&
            e.target?.closest?.(`[data-hook~=${MODAL_ROOT}]`) !== null
          ) {
            return;
          }

          if (modals.getModal(LINK_MODAL_ID)) {
            modals.closeModal(LINK_MODAL_ID);
          }

          if (referenceElement?.contains?.(e.target as Node)) {
            return;
          }

          modals.closeModal(modalId);
        },
      },
    });
  };

  return [
    {
      id: 'gallery_add_media',
      dataHook: PLUGIN_TOOLBAR_ADD_MEDIA_BUTTON,
      command: ({ attributes: { selectedNode }, publicCommands }) => {
        handleGalleryUpload({ publicCommands, services, config, nodeId: selectedNode.attrs.id });
      },
      attributes: {
        selectedNode: selectedNodeResolver,
      },
      renderer: toolbarItem => <GalleryAddMediaButton toolbarItem={toolbarItem} />,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: GALLERY_MEDIA_SETTINGS_MODAL,
      dataHook: PLUGIN_TOOLBAR_MANAGE_MEDIA_BUTTON,
      command: ({
        referenceElement,
        clickFromKeyboard,
        attributes: { selectedNode },
        publicCommands,
      }) => {
        toggleSettingsModal({
          modalId: GALLERY_MEDIA_SETTINGS_MODAL,
          node: selectedNode,
          referenceElement,
          activeTab: SETTINGS_TAB.design,
          clickFromKeyboard,
          publicCommands,
        });
      },
      attributes: {
        selectedNode: selectedNodeResolver,
        active: {
          id: 'IS_GALLERY_MEDIA_SETTINGS_BUTTON_ACTIVE',
          resolve: (_, { modals }) => Boolean(modals.getModal(GALLERY_MEDIA_SETTINGS_MODAL)),
        },
      },
      renderer: toolbarItem => <GalleryManageMediaButton toolbarItem={toolbarItem} />,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: GALLERY_LAYOUT_DROPDOWN,
      dataHook: PLUGIN_TOOLBAR_LAYOUT_DROPDOWN_BUTTON,
      command: ({
        isMobile,
        clickFromKeyboard,
        referenceElement,
        tiptapEditor,
        attributes,
        publicCommands,
      }) => {
        if (modals?.isModalOpen(GALLERY_LAYOUT_DROPDOWN)) {
          modals?.closeModal(GALLERY_LAYOUT_DROPDOWN);
          return;
        }

        if (modals?.isModalOpen(GALLERY_LAYOUT_SETTINGS_MODAL)) {
          modals?.closeModal(GALLERY_LAYOUT_SETTINGS_MODAL);
        }

        modals?.openModal({
          id: GALLERY_LAYOUT_DROPDOWN,
          Component: LayoutDropdown,
          componentProps: {
            selectedLayout: attributes?.layout ?? GalleryOptions_Layout_Type.GRID,
            onLayoutSelect: layout => {
              const {
                selection: { anchor: pos },
              } = tiptapEditor.state;
              tiptapEditor
                .chain()
                .focus()
                .updateAttributes(TIPTAP_GALLERY_TYPE, {
                  options: layoutRicosData[layout],
                })
                .run();
              setTimeout(() => tiptapEditor.chain().focus().setNodeSelection(pos).run(), 50);
              modals?.closeModal(GALLERY_LAYOUT_DROPDOWN);
            },
            onCustomizeButtonClick: ({ clickFromKeyboard }) => {
              toggleSettingsModal({
                modalId: GALLERY_LAYOUT_SETTINGS_MODAL,
                node: attributes.selectedNode,
                referenceElement,
                activeTab: SETTINGS_TAB.layout,
                clickFromKeyboard,
                publicCommands,
              });
              modals?.closeModal(GALLERY_LAYOUT_DROPDOWN);
            },
          },
          layout: isMobile ? 'drawer' : 'toolbar',
          positioning: { referenceElement, placement: 'bottom' },
          returnFocusOnDeactivate: clickFromKeyboard,
        });
      },
      attributes: {
        layout: selectedLayoutResolver,
        selectedNode: selectedNodeResolver,
        active: {
          id: 'IS_GALLERY_LAYOUT_SETTINGS_BUTTON_ACTIVE',
          resolve: (_, { modals }) =>
            Boolean(modals.getModal(GALLERY_LAYOUT_DROPDOWN)) ||
            Boolean(modals.getModal(GALLERY_LAYOUT_SETTINGS_MODAL)),
        },
      },
      renderer: toolbarItem => <GalleryLayoutButton toolbarItem={toolbarItem} />,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SIZE,
      modal: {
        Component: SizePanel,
        id: PLUGIN_TOOLBAR_BUTTON_ID.SIZE,
      },
      renderer: toolbarItem => (
        <NodeSizeButton id={PLUGIN_TOOLBAR_BUTTON_ID.SIZE} toolbarItem={toolbarItem} />
      ),
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
      attributes: {
        visible: getVisibleOnlyOnDesktopResolver(isMobile),
      },
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.ALIGNMENT,
      modal: {
        Component: AlignmentPanel,
        id: PLUGIN_TOOLBAR_BUTTON_ID.ALIGNMENT,
      },
      renderer: toolbarItem => (
        <NodeAlignmentButton toolbarItem={toolbarItem} id={PLUGIN_TOOLBAR_BUTTON_ID.ALIGNMENT} />
      ),
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
      attributes: {
        visible: getVisibleOnlyOnDesktopResolver(isMobile),
      },
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SETTINGS,
      attributes: {
        active: {
          id: 'IS_GALLERY_SETTINGS_BUTTON_ACTIVE',
          resolve: (_, { modals }) => Boolean(modals.getModal(GALLERY_SETTINGS_MODAL_ID)),
        },
      },
      command: ({
        referenceElement,
        clickFromKeyboard,
        attributes: { selectedNode },
        publicCommands,
      }) => {
        toggleSettingsModal({
          modalId: GALLERY_SETTINGS_MODAL_ID,
          node: selectedNode,
          referenceElement,
          activeTab: SETTINGS_TAB.settings,
          clickFromKeyboard,
          publicCommands,
        });
      },
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.DELETE,
    },
  ];
};
